import React, { useState } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    alpha,
    useTheme,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Whatshot as FlareIcon,
    TrendingUp as TrendingIcon,
    OpenInNew as OpenInNewIcon,
    Create as CreateIcon,
    Article as ArticleIcon,
    Tag as TagIcon,
    PlayCircle as PlayCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Platform accent colours
const PLATFORM_COLORS = {
    'Google Trends':       '#4285F4',
    'YouTube Trending':    '#FF0000',
    'Instagram':           '#E1306C',
    'Facebook':            '#1877F2',
};

function getAccentColor(sourceLabel = '') {
    for (const [key, color] of Object.entries(PLATFORM_COLORS)) {
        if (sourceLabel.toLowerCase().includes(key.toLowerCase())) return color;
    }
    return null; // falls back to theme primary
}

// Format a pub date string into "2h ago", "3d ago" etc.
function timeAgo(dateStr) {
    if (!dateStr) return '';
    try {
        const d   = new Date(dateStr);
        const now = Date.now();
        const sec = Math.floor((now - d) / 1000);
        if (sec < 60)   return `${sec}s ago`;
        if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
        if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
        return `${Math.floor(sec / 86400)}d ago`;
    } catch {
        return '';
    }
}

// Thumbnail with fallback letter avatar
function Thumbnail({ src, topic, accentColor, isVideo }) {
    const [failed, setFailed] = useState(false);
    const theme = useTheme();
    if (!src || failed) {
        return (
            <Box sx={{
                width: '100%',
                aspectRatio: '16/9',
                bgcolor: alpha(accentColor || theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px 12px 0 0',
                flexShrink: 0,
            }}>
                {isVideo
                    ? <PlayCircleIcon sx={{ fontSize: 40, color: accentColor || theme.palette.primary.main, opacity: 0.5 }} />
                    : <ArticleIcon    sx={{ fontSize: 40, color: accentColor || theme.palette.primary.main, opacity: 0.5 }} />
                }
            </Box>
        );
    }
    return (
        <Box sx={{ position: 'relative', borderRadius: '12px 12px 0 0', overflow: 'hidden', flexShrink: 0 }}>
            <img
                src={src}
                alt={topic}
                onError={() => setFailed(true)}
                style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
            />
            {isVideo && (
                <Box sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.25)',
                }}>
                    <PlayCircleIcon sx={{ fontSize: 44, color: '#fff', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                </Box>
            )}
        </Box>
    );
}

const TrendCard = ({ item, index }) => {
    const theme    = useTheme();
    const navigate = useNavigate();
    const accent   = getAccentColor(item.source_label) || theme.palette.primary.main;
    const isVideo  = item.source_label?.toLowerCase().includes('youtube') || !!item.video_id;


    const handleCreatePost = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const hashtags = (item.hashtags || []).join(' ');
        const caption  = `${item.topic}\n\n${hashtags}`;
        navigate('/create', { state: { prefill: { caption } } });
    };

    return (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    borderRadius: '16px',
                    background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.7)
                        : '#ffffff',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 16px 40px ${alpha(accent, 0.18)}`,
                        borderColor: alpha(accent, 0.35),
                        '& .card-actions': { opacity: 1, transform: 'translateY(0)' },
                    },
                }}
            >
                {/* Rank badge */}
                <Box sx={{
                    position: 'absolute', top: 10, left: 10, zIndex: 3,
                    width: 28, height: 28, borderRadius: '8px',
                    bgcolor: index === 0 ? accent : alpha(theme.palette.background.paper, 0.9),
                    color:   index === 0 ? '#fff' : theme.palette.text.secondary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', fontWeight: 900,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(4px)',
                }}>
                    {index + 1}
                </Box>

                {/* Hover action buttons */}
                <Box
                    className="card-actions"
                    sx={{
                        position: 'absolute', top: 10, right: 10, zIndex: 3,
                        display: 'flex', gap: 0.75,
                        opacity: 0,
                        transform: 'translateY(-4px)',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Tooltip title="Create post from this trend" placement="top">
                        <IconButton
                            size="small"
                            onClick={handleCreatePost}
                            sx={{
                                bgcolor: accent, color: '#fff',
                                width: 30, height: 30,
                                '&:hover': { bgcolor: accent, filter: 'brightness(1.1)' },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                            }}
                        >
                            <CreateIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Open source" placement="top">
                        <IconButton
                            size="small"
                            component="a"
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                bgcolor: alpha(theme.palette.background.paper, 0.9),
                                color: theme.palette.text.secondary,
                                width: 30, height: 30,
                                backdropFilter: 'blur(4px)',
                                '&:hover': { bgcolor: theme.palette.background.paper },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                        >
                            <OpenInNewIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Thumbnail */}
                <Thumbnail
                    src={item.picture_url}
                    topic={item.topic}
                    accentColor={accent}
                    isVideo={isVideo}
                />

                <CardContent sx={{ p: 2.5, pb: '2.5 !important', display: 'flex', flexDirection: 'column', flex: 1, gap: 1.5 }}>

                    {/* Status chips */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<FlareIcon sx={{ fontSize: '13px !important', color: '#f97316 !important' }} />}
                            label="Trending"
                            size="small"
                            sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 800,
                                bgcolor: alpha('#f97316', 0.1), color: '#f97316',
                                border: 'none', pl: 0.5,
                            }}
                        />
                        {item.traffic && (
                            <Chip
                                icon={<TrendingIcon sx={{ fontSize: '12px !important', color: `${accent} !important` }} />}
                                label={item.traffic}
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.65rem', fontWeight: 800,
                                    bgcolor: alpha(accent, 0.1), color: accent,
                                    border: 'none', pl: 0.5,
                                }}
                            />
                        )}
                        {item.category && item.category !== 'Trending' && (
                            <Chip
                                label={item.category}
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.6rem', fontWeight: 700,
                                    bgcolor: alpha(theme.palette.text.primary, 0.05),
                                    color: 'text.secondary', border: 'none',
                                }}
                            />
                        )}
                    </Box>

                    {/* Topic title */}
                    <Typography
                        variant="subtitle1"
                        component="a"
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            fontWeight: 800,
                            lineHeight: 1.35,
                            letterSpacing: '-0.01em',
                            color: 'text.primary',
                            textDecoration: 'none',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            '&:hover': { color: accent },
                            transition: 'color 0.2s',
                        }}
                    >
                        {item.topic}
                    </Typography>

                    {/* News snippets (up to 2) */}
                    {item.news && item.news.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.25 }}>
                            {item.news.slice(0, 2).map((n, i) => (
                                <Box
                                    key={i}
                                    component="a"
                                    href={n.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'flex-start',
                                        textDecoration: 'none',
                                        p: 1,
                                        borderRadius: '8px',
                                        bgcolor: alpha(theme.palette.divider, 0.03),
                                        border: `1px solid ${alpha(theme.palette.divider, 0.07)}`,
                                        '&:hover': { bgcolor: alpha(accent, 0.04), borderColor: alpha(accent, 0.2) },
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {n.picture && (
                                        <Box
                                            component="img"
                                            src={n.picture}
                                            alt=""
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                            sx={{ width: 36, height: 36, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                                        />
                                    )}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem', fontWeight: 600,
                                                color: 'text.primary', lineHeight: 1.35,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {n.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.3, alignItems: 'center' }}>
                                            {n.source && (
                                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: accent }}>
                                                    {n.source}
                                                </Typography>
                                            )}
                                            {n.time && (
                                                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                                                    {timeAgo(n.time) || n.time}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Hashtags */}
                    {item.hashtags && item.hashtags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TagIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                            {item.hashtags.slice(0, 3).map((tag) => (
                                <Typography
                                    key={tag}
                                    sx={{
                                        fontSize: '0.65rem', fontWeight: 700,
                                        color: alpha(accent, 0.8),
                                        cursor: 'default',
                                    }}
                                >
                                    {tag}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {/* Footer */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        pt: 1.5,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.07)}`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                sx={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${alpha(accent, 0.2)}`,
                                }}
                            />
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' }}>
                                {item.source_label || 'Trends'}
                            </Typography>
                        </Box>
                        {item.channel && (
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.channel}
                            </Typography>
                        )}
                        {item.published && !item.channel && (
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                                {timeAgo(item.published)}
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
};

export default TrendCard;
